import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PartnersService } from '~/app/services/partners/partners.service';
import type {
  MaintenanceRulesStatus,
  MaintenanceSlaContractRecord,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';

export function partnerMaintenanceRulesKey(organizationId: string) {
  return ['partners', 'maintenance-rules', organizationId] as const;
}

export function usePartnerMaintenanceRules(organizationId: string) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: partnerMaintenanceRulesKey(organizationId),
    queryFn: () => PartnersService.listPartnerMaintenanceRules(organizationId),
    enabled: organizationId.length > 0,
  });

  const contracts = useMemo(() => {
    const map: Record<string, MaintenanceSlaContractRecord> = {};
    for (const item of query.data ?? []) {
      map[item.relationshipId] = item;
    }
    return map;
  }, [query.data]);

  const save = useCallback(
    async (
      record: MaintenanceSlaContractRecord,
      rulesStatus: MaintenanceRulesStatus,
    ): Promise<MaintenanceSlaContractRecord> => {
      const saved = await PartnersService.replacePartnerMaintenanceRules(
        organizationId,
        record,
        rulesStatus,
      );
      queryClient.setQueryData<MaintenanceSlaContractRecord[]>(
        partnerMaintenanceRulesKey(organizationId),
        (current) => {
          const items = current ?? [];
          return [
            ...items.filter(
              (item) => item.relationshipId !== saved.relationshipId,
            ),
            saved,
          ];
        },
      );
      return saved;
    },
    [organizationId, queryClient],
  );

  return {
    contracts,
    isLoading: query.isLoading,
    save,
  };
}
